import { useState, useCallback, useRef, useEffect } from 'react';
import { Room, RoomEvent, ParticipantEvent, Track, LocalParticipant, RemoteParticipant } from 'livekit-client';
import { v4 as uuidv4 } from 'uuid';
import { simpleLiveStudioApi } from '../services/simpleLivestudioApiService';
import {
  RoomState,
  Participant,
  ConnectionState,
  VideoElementRefs,
  TokenPermissions,
  RoomConfig
} from '../types';
import {
  CONNECTION_TIMEOUT,
  MAX_CONNECTION_ATTEMPTS,
  RETRY_DELAY_BASE,
  CONNECTION_CHECK_INTERVAL,
  VIDEO_RESOLUTION,
  AUDIO_CONSTRAINTS,
  ERROR_MESSAGES,
  SUCCESS_MESSAGES,
  TRACK_ATTACH_DELAY,
  VIDEO_TRACK_WAIT_DELAY
} from '../constants';

const config = require('config/app.config');

interface UseRoomConnectionProps {
  profile: { token: string; code?: string };
  videoRefs: VideoElementRefs;
  onParticipantsUpdate: (participants: Participant[]) => void;
  onError: (error: string) => void;
}

export const useRoomConnection = ({
  profile,
  videoRefs,
  onParticipantsUpdate,
  onError
}: UseRoomConnectionProps) => {
  const [roomState, setRoomState] = useState<RoomState>({
    room: null,
    participants: [],
    localParticipant: null,
    isConnecting: false,
    error: null,
    showPreJoin: true,
    roomName: '',
    showLeaveConfirmation: false,
    livekitToken: null,
  });

  const [connectionState, setConnectionState] = useState<ConnectionState>({
    hasConnected: false,
    isConnecting: false,
    connectionAttempts: 0,
  });

  const connectionTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isComponentMounted = useRef<boolean>(true);
  const isConnectingRef = useRef<boolean>(false);
  const hasConnectedRef = useRef<boolean>(false);

  // Check media permissions
  const checkMediaPermissions = useCallback(async (): Promise<{ hasPermissions: boolean; error?: string }> => {
    try {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error(ERROR_MESSAGES.MEDIA_DEVICES_NOT_AVAILABLE);
      }

      // Check if we have permissions without actually requesting them
      if ('permissions' in navigator) {
        try {
          const cameraPermission = await navigator.permissions.query({ name: 'camera' as PermissionName });
          const micPermission = await navigator.permissions.query({ name: 'microphone' as PermissionName });

          if (cameraPermission.state === 'denied' || micPermission.state === 'denied') {
            return {
              hasPermissions: false,
              error: 'Camera and microphone permissions have been denied. Please click the camera/microphone icon in your browser\'s address bar and allow permissions, then refresh the page.'
            };
          }
        } catch (err) {
          console.warn('Permissions API not supported, falling back to getUserMedia check');
        }
      }

      // Test access with constraints
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user', width: { ideal: VIDEO_RESOLUTION.width }, height: { ideal: VIDEO_RESOLUTION.height } },
        audio: true
      });

      // Stop the stream immediately as we only want to check permissions
      stream.getTracks().forEach(track => track.stop());
      return { hasPermissions: true };
    } catch (err: any) {
      console.error('Media permissions denied:', err);

      // Provide specific error messages based on the error type
      if (err.name === 'NotAllowedError') {
        return {
          hasPermissions: false,
          error: 'Camera and microphone permissions have been denied. Please click the camera/microphone icon in your browser\'s address bar and allow permissions, then refresh the page.'
        };
      } else if (err.name === 'NotFoundError') {
        return {
          hasPermissions: false,
          error: 'No camera or microphone found. Please connect a camera and microphone and try again.'
        };
      } else if (err.name === 'NotReadableError') {
        return {
          hasPermissions: false,
          error: 'Camera or microphone is already in use by another application. Please close other applications and try again.'
        };
      } else {
        return {
          hasPermissions: false,
          error: ERROR_MESSAGES.MEDIA_PERMISSIONS_DENIED
        };
      }
    }
  }, []);

  // Update participants list
  const updateParticipants = useCallback((livekitRoom: Room) => {
    console.log('Updating participants list');
    const participantsList: Participant[] = [];

    // Add local participant
    if (livekitRoom.localParticipant) {
      participantsList.push({
        id: livekitRoom.localParticipant.sid,
        identity: livekitRoom.localParticipant.identity,
        name: livekitRoom.localParticipant.name || 'You',
        isLocal: true,
        isSpeaking: livekitRoom.localParticipant.isSpeaking,
        connectionQuality: livekitRoom.localParticipant.connectionQuality,
        videoEnabled: livekitRoom.localParticipant.isCameraEnabled,
        audioEnabled: livekitRoom.localParticipant.isMicrophoneEnabled,
        isScreenSharing: livekitRoom.localParticipant.isScreenShareEnabled
      });
    }

    // Add remote participants
    livekitRoom.remoteParticipants.forEach((participant) => {
      participantsList.push({
        id: participant.sid,
        identity: participant.identity,
        name: participant.name || participant.identity,
        isLocal: false,
        isSpeaking: participant.isSpeaking,
        connectionQuality: participant.connectionQuality,
        videoEnabled: Array.from(participant.videoTrackPublications.values()).some(pub => pub.isEnabled),
        audioEnabled: Array.from(participant.audioTrackPublications.values()).some(pub => pub.isEnabled),
        isScreenSharing: Array.from(participant.videoTrackPublications.values()).some(pub =>
          pub.isEnabled && pub.source === Track.Source.ScreenShare)
      });
    });

    setRoomState(prev => ({ ...prev, participants: participantsList }));
    onParticipantsUpdate(participantsList);
  }, [onParticipantsUpdate]);

  // Attach video track to element
  const attachVideoTrack = useCallback((track: Track | undefined, participantId: string, isLocal: boolean = false) => {
    try {
      if (!track) {
        console.warn('Track is undefined, cannot attach');
        return;
      }

      console.log('Attaching video track for participant:', participantId, 'isLocal:', isLocal);
      console.log('Track details:', {
        kind: track.kind,
        source: (track as any).source,
        sid: track.sid,
        enabled: (track as any).isEnabled
      });

      if (isLocal) {
        console.log('Local video ref current:', videoRefs.local.current);
        if (videoRefs.local.current) {
          console.log('Attaching local track to video element');
          track.attach(videoRefs.local.current);
          console.log('Attached local track to video element');
          console.log('Video element srcObject after attach:', videoRefs.local.current.srcObject);
        } else {
          console.log('Local video ref is null, cannot attach track - will retry in 100ms');
          // Retry after a short delay to allow the video element to be created
          setTimeout(() => {
            if (videoRefs.local.current) {
              console.log('Retrying track attachment after delay');
              track.attach(videoRefs.local.current);
            } else {
              console.log('Video ref still null after retry');
            }
          }, 100);
        }
      } else {
        const videoElement = videoRefs.remote.current[participantId];
        console.log('Remote video element for participant:', participantId, videoElement);
        if (videoElement) {
          track.attach(videoElement);
          console.log('Attached remote track to video element for participant:', participantId);
        } else {
          // Try to find element in DOM
          const domElement = document.querySelector(`video[data-participant-id="${participantId}"]`);
          console.log('Found DOM element for remote participant:', domElement);
          if (domElement) {
            track.attach(domElement as HTMLMediaElement);
            console.log('Attached remote track to DOM element');
          }
        }
      }
    } catch (error) {
      console.error('Error attaching video track:', error);
    }
  }, [videoRefs]);

  // Setup room event listeners
  const setupRoomEventListeners = useCallback((livekitRoom: Room) => {
    livekitRoom.on(RoomEvent.ParticipantConnected, () => {
      updateParticipants(livekitRoom);
    });

    livekitRoom.on(RoomEvent.ParticipantDisconnected, () => {
      updateParticipants(livekitRoom);
    });

    livekitRoom.on(RoomEvent.TrackSubscribed, (track, publication, participant) => {
      console.log('Track subscribed:', {
        kind: track?.kind,
        source: (track as any)?.source,
        sid: publication.trackSid,
        participantSid: participant.sid,
        participantIdentity: participant.identity,
        isEnabled: publication?.isEnabled
      });

      if (track && track.kind === Track.Kind.Video) {
        requestAnimationFrame(() => {
          const isLocal = !!(livekitRoom && livekitRoom.localParticipant && participant.sid === livekitRoom.localParticipant.sid);
          attachVideoTrack(track, participant.sid, isLocal);
        });
      }
      updateParticipants(livekitRoom);
    });

    livekitRoom.on(RoomEvent.TrackUnsubscribed, (track, publication, participant) => {
      console.log('Track unsubscribed:', track?.kind, 'for participant:', participant.sid);
      if (track && track.kind === Track.Kind.Video) {
        if (livekitRoom?.localParticipant && participant.sid === livekitRoom.localParticipant.sid) {
          if (videoRefs.local.current) {
            track.detach(videoRefs.local.current);
          }
        } else {
          const videoElement = videoRefs.remote.current[participant.sid];
          if (videoElement) {
            track.detach(videoElement);
          }
        }
      }
      updateParticipants(livekitRoom);
    });

    livekitRoom.on(RoomEvent.ActiveSpeakersChanged, () => {
      updateParticipants(livekitRoom);
    });

    livekitRoom.on(RoomEvent.ConnectionStateChanged, (state) => {
      console.log('Connection state changed:', state);
    });

    livekitRoom.on(RoomEvent.Disconnected, (reason) => {
      console.log('Disconnected from room:', reason);
      hasConnectedRef.current = false;
      isConnectingRef.current = false;
      setConnectionState(prev => ({
        ...prev,
        hasConnected: false,
        isConnecting: false
      }));
    });
  }, [updateParticipants, attachVideoTrack, videoRefs]);

  // Enable camera and microphone with retry logic
  const enableMediaWithRetry = useCallback(async (livekitRoom: Room) => {
    if (!livekitRoom?.localParticipant) return;

    const canPublish = livekitRoom.localParticipant.permissions?.canPublish;
    if (!canPublish) {
      throw new Error(ERROR_MESSAGES.NO_PUBLISH_PERMISSION);
    }

    let retryCount = 0;
    const maxRetries = MAX_CONNECTION_ATTEMPTS;
    let enableSuccess = false;

    while (retryCount < maxRetries && !enableSuccess) {
      try {
        console.log(`Attempting to enable camera and microphone (attempt ${retryCount + 1}/${maxRetries})`);
        await livekitRoom.localParticipant.enableCameraAndMicrophone();
        console.log('Camera and microphone enabled successfully');
        enableSuccess = true;
      } catch (enableError: any) {
        retryCount++;
        console.warn(`Failed to enable camera/microphone (attempt ${retryCount}):`, enableError.message);

        if (retryCount < maxRetries) {
          await new Promise(resolve => setTimeout(resolve, RETRY_DELAY_BASE * retryCount));
        } else {
          throw enableError;
        }
      }
    }

    // Force attach local video track if available
    const videoPublications = Array.from(livekitRoom.localParticipant.videoTrackPublications.values());
    if (videoPublications.length > 0 && videoPublications[0].track) {
      console.log('Force attaching local video track');
      setTimeout(() => {
        attachVideoTrack(videoPublications[0].track, livekitRoom.localParticipant!.sid, true);
      }, TRACK_ATTACH_DELAY);
    }

    updateParticipants(livekitRoom);
  }, [attachVideoTrack, updateParticipants]);

  // Connect to room
  const connectToRoom = useCallback(async (roomId: string) => {
    console.log('connectToRoom called with roomId:', roomId);

    if (!roomId) {
      console.error('No roomId provided to connectToRoom');
      onError(ERROR_MESSAGES.ROOM_ID_REQUIRED);
      return;
    }

    // Check current connection state to avoid duplicate connections
    if (isConnectingRef.current || hasConnectedRef.current) {
      console.log('Connection already in progress or completed, skipping', {
        isConnecting: isConnectingRef.current,
        hasConnected: hasConnectedRef.current
      });
      return;
    }

    isConnectingRef.current = true;
    setConnectionState(prev => ({ ...prev, isConnecting: true }));
    setRoomState(prev => ({ ...prev, isConnecting: true, error: null }));

    let livekitRoom: Room | null = null;

    try {
      // Check media permissions first
      const permissionResult = await checkMediaPermissions();
      if (!permissionResult.hasPermissions) {
        throw new Error(permissionResult.error || ERROR_MESSAGES.MEDIA_PERMISSIONS_DENIED);
      }

      // Get token from backend
      const token = await simpleLiveStudioApi.generateToken(
        profile.token,
        roomId,
        `participant_${uuidv4()}`,
        'Participant',
        {
          canPublish: true,
          canSubscribe: true,
          canPublishData: true,
          roomRecord: true,
          roomAdmin: true
        } as TokenPermissions
      );

      setRoomState(prev => ({ ...prev, livekitToken: token }));

      // Create room with configuration
      const roomConfig: RoomConfig = {
        adaptiveStream: true,
        dynacast: true,
        videoCaptureDefaults: {
          resolution: VIDEO_RESOLUTION,
        },
        audioCaptureDefaults: AUDIO_CONSTRAINTS,
      };

      livekitRoom = new Room(roomConfig);

      // Setup event listeners
      setupRoomEventListeners(livekitRoom);

      // Connect to room
      console.log('Connecting to room:', config.livekit.ws_url);
      await livekitRoom.connect(config.livekit.ws_url, token, {
        autoSubscribe: true,
      });
      console.log(SUCCESS_MESSAGES.CONNECTED);

      hasConnectedRef.current = true;
      setConnectionState(prev => ({ ...prev, hasConnected: true }));

      console.log('Room connected successfully:', {
        roomState: livekitRoom?.state,
        hasLocalParticipant: !!livekitRoom?.localParticipant,
        localParticipantSid: livekitRoom?.localParticipant?.sid,
        localParticipantIdentity: livekitRoom?.localParticipant?.identity
      });

      setRoomState(prev => ({
        ...prev,
        room: livekitRoom,
        localParticipant: livekitRoom?.localParticipant || null,
        showPreJoin: false
      }));

      // Wait for WebRTC connection
      const waitForConnection = new Promise<void>((resolve, reject) => {
        const timeout = setTimeout(() => {
          reject(new Error(ERROR_MESSAGES.CONNECTION_TIMEOUT));
        }, CONNECTION_TIMEOUT);

        const checkConnection = () => {
          if (livekitRoom && livekitRoom.state === 'connected') {
            clearTimeout(timeout);
            resolve();
          } else {
            console.log('Current connection state:', livekitRoom?.state);
            setTimeout(checkConnection, CONNECTION_CHECK_INTERVAL);
          }
        };

        checkConnection();
      });

      await waitForConnection;
      console.log('WebRTC peer connection established successfully');

      // Enable media
      await enableMediaWithRetry(livekitRoom);

      // Final state check
      console.log('Final connection state:', {
        roomState: livekitRoom?.state,
        hasLocalParticipant: !!livekitRoom?.localParticipant,
        localParticipantSid: livekitRoom?.localParticipant?.sid,
        isCameraEnabled: livekitRoom?.localParticipant?.isCameraEnabled,
        isMicrophoneEnabled: livekitRoom?.localParticipant?.isMicrophoneEnabled
      });

    } catch (err: any) {
      if (livekitRoom) {
        try {
          await livekitRoom.disconnect();
        } catch (disconnectError) {
          console.error('Error disconnecting from room:', disconnectError);
        }
      }
      const errorMessage = err.message || ERROR_MESSAGES.FAILED_TO_CONNECT;
      setRoomState(prev => ({ ...prev, error: errorMessage }));
      onError(errorMessage);
    } finally {
      isConnectingRef.current = false;
      setConnectionState(prev => ({ ...prev, isConnecting: false }));
      setRoomState(prev => ({ ...prev, isConnecting: false }));
    }
  }, [profile.token, checkMediaPermissions, setupRoomEventListeners, enableMediaWithRetry, onError]);

  // Disconnect from room
  const disconnectFromRoom = useCallback(async () => {
    if (roomState.room) {
      try {
        await roomState.room.disconnect();
      } catch (error) {
        console.error('Error disconnecting from room:', error);
      } finally {
        setRoomState(prev => ({
          ...prev,
          room: null,
          localParticipant: null,
          participants: [],
          livekitToken: null,
        }));
        hasConnectedRef.current = false;
        isConnectingRef.current = false;
        setConnectionState(prev => ({
          ...prev,
          hasConnected: false,
          isConnecting: false
        }));
      }
    }
  }, [roomState.room]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      isComponentMounted.current = false;
      if (connectionTimeoutRef.current) {
        clearTimeout(connectionTimeoutRef.current);
      }
      if (roomState.room) {
        roomState.room.disconnect();
      }
    };
  }, [roomState.room]);

  return {
    roomState,
    connectionState,
    connectToRoom,
    disconnectFromRoom,
    updateParticipants,
    attachVideoTrack,
  };
};
